/**
 * The capability registry: the one place that names every API module, every nav
 * section, which module backs each section, which roles may enter it, and where
 * it sits in the navigation — which group, in what order, and whether it earns
 * a tab on the phone.
 *
 * Deliberately zod-free. Browser code imports it as `@jaks/contract/registry`
 * without pulling zod into the bundle; the main contract entry re-exports it
 * for the server. Everything that used to keep its own copy of these lists —
 * the web manifest, the API module table, the old VIRTUAL_SECTIONS constant,
 * the old Today/Work/Sell/Buy/Company groups in the shell, the Permissions
 * matrix — reads them from here, and drift tests fail the build if a list grows
 * a member this file has never heard of.
 */

/** Named roles. `standard` is implicit for any signed-in @jaks.construction person. */
export const ROLES = [
  "standard", "field", "sales", "hr", "pm", "marketing", "accounting", "admin", "executive",
] as const;
export type Role = (typeof ROLES)[number];

/**
 * How a role is written on screen, and it is deliberately the GROUP's name.
 *
 * Since T16 a role is membership of a Google group, so a screen that calls the
 * column something the Admin console does not call it is asking somebody to
 * translate between two names for one thing. Each label is the part of the
 * address after `role-`, capitalised: `role-pm@` is PM, `role-accounting@` is
 * Accounting. A registry test pins that, so a label and its group cannot drift.
 *
 * Capitalising the id alone would give "Pm" and "Hr", which is why this map
 * exists at all rather than a `toUpperCase` on the first letter.
 *
 * **`standard` is the exception, and it is why it is called Team.** It has no
 * `role-*` group and cannot have one: it is what being signed in means, and the
 * population it describes is exactly `team@jaks.construction` — which is a
 * mailing list rather than a permission, is never read by the derivation, and
 * could not be even if somebody wanted it to (a Security group may only contain
 * Security groups, so team@ can never be nested inside one). Calling the column
 * "Standard" made it look like a ninth role somebody could be given. Calling it
 * Team says who is in it. Every screen that shows it says the rest.
 */
export const ROLE_LABELS: Record<Role, string> = {
  standard: "Team", field: "Field", sales: "Sales", hr: "HR", pm: "PM",
  marketing: "Marketing", accounting: "Accounting", admin: "Admin", executive: "Executive",
};

/**
 * The group behind a role, written for a person: the address for the eight that
 * have one, and the plain truth for the one that does not.
 */
export function groupNoteFor(role: Role): string {
  return role === "standard"
    ? "everyone signed in — team@jaks.construction, which is a mailing list rather than a permission"
    : ROLE_GROUPS[role as AssignableRole];
}

/**
 * Roles this platform used to name and no longer does. A stored `module_access`
 * override written before a role was retired still holds it, so both the read
 * (`knownModuleAccess`) and the write (`normalizeModuleAccess` in the settings
 * module) drop these silently rather than refusing the save — a retired role is
 * not a typo, and refusing would leave an admin unable to save the very matrix
 * that would clean the row.
 *
 * `coordinator` was retired in T15: nobody held it (checked live 2026-08-31),
 * `pm` had been added everywhere it appeared, and it had drifted into meaning
 * "the office" rather than a person. The job ASSIGNMENT slot of the same name
 * is a different vocabulary and still exists — see `JOB_ASSIGNMENT_ROLES` in
 * job-control.ts, which is what a job's coordinator column stores.
 */
export const RETIRED_ROLES = ["coordinator"] as const;
export type RetiredRole = (typeof RETIRED_ROLES)[number];

/** True for a role this platform used to have and has since dropped. */
export function isRetiredRole(r: unknown): r is RetiredRole {
  return typeof r === "string" && (RETIRED_ROLES as readonly string[]).includes(r);
}

/** Every role a Google group backs. `standard` is not one: it is what being signed in means. */
export type AssignableRole = Exclude<Role, "standard">;

/**
 * The Google Workspace group that GRANTS each role, by address.
 *
 * These groups are the source of truth for platform roles (T16). Membership of
 * `role-pm@` is what makes somebody a PM; `core.people.roles` is a mirror of
 * that membership, refreshed by the daily reconciliation and by every write the
 * platform makes, and it is the only thing authorization ever reads. Google is
 * never asked a question at request time.
 *
 * **`standard` has no group and never will.** It is not granted, it is what any
 * signed-in @jaks.construction person already holds — `rolesFor()` adds it at
 * session time. A group for it would be a group everybody is in, which is
 * team@, which is a mailing list rather than a permission.
 *
 * **Only these eight addresses are ever read.** `team@` is a mail-only group and
 * is never consulted; the `access-*` groups exist to share Drive folders and are
 * never consulted either. Reading a group that is not in this map cannot grant a
 * role, because the derivation walks THIS map rather than whatever Google
 * happens to return.
 *
 * A completeness test pins `Object.keys(ROLE_GROUPS)` to `ROLES` minus
 * `standard`, so adding a role without creating its group fails the build rather
 * than quietly granting nobody anything. The retirement direction is covered
 * too: a retired role must not keep an address here.
 */
export const ROLE_GROUPS: Record<AssignableRole, string> = {
  field: "role-field@jaks.construction",
  sales: "role-sales@jaks.construction",
  hr: "role-hr@jaks.construction",
  pm: "role-pm@jaks.construction",
  marketing: "role-marketing@jaks.construction",
  accounting: "role-accounting@jaks.construction",
  admin: "role-admin@jaks.construction",
  executive: "role-executive@jaks.construction",
};

/** The eight addresses, lowercased, in ROLES order. What the reconciliation walks. */
export const ROLE_GROUP_EMAILS: readonly string[] = ROLES.filter(
  (r): r is AssignableRole => r !== "standard",
).map((r) => ROLE_GROUPS[r]);

/**
 * The role a group address grants, or null for any address that grants nothing.
 *
 * Null is the answer for `team@`, for both `access-*` groups, and for anything
 * else a person happens to be in. Comparison is case-insensitive because Google
 * echoes back whatever case the address was created with.
 */
export function roleForGroup(email: string): AssignableRole | null {
  const key = email.trim().toLowerCase();
  for (const [role, address] of Object.entries(ROLE_GROUPS) as Array<[AssignableRole, string]>) {
    if (address.toLowerCase() === key) return role;
  }
  return null;
}

export interface ApiModuleDef {
  /** How the module is named to an admin in the Permissions matrix. */
  label: string;
  /** Roles that may enter. Admins always pass; empty means everyone signed in. */
  access: readonly Role[];
  /** Rows the matrix refuses to edit: `home` is the front door, `settings` is the lock. */
  locked?: "everyone" | "admin";
}

/**
 * Every API module, keyed by the id it mounts under at `/api/<id>`. This table
 * and `FACTORIES` in apps/api/src/modules.ts must name the same set; a test
 * there enforces it.
 */
const API_MODULE_TABLE = {
  home: { label: "Home", access: [], locked: "everyone" },
  time: { label: "Time", access: [] },
  sales: { label: "Sales", access: ["admin", "sales", "pm", "executive", "marketing"] },
  projects: { label: "Projects", access: [] },
  field: { label: "Field", access: [] },
  fleet: { label: "Fleet", access: [] },
  estimating: { label: "Estimating", access: ["admin", "sales", "pm", "executive"] },
  reports: { label: "Reports", access: ["admin", "sales", "pm", "executive", "marketing", "accounting"] },
  vendors: { label: "Vendors", access: ["admin", "sales", "pm", "executive", "accounting"] },
  learn: { label: "Learn", access: [] },
  people: { label: "People", access: [] },
  hr: { label: "HR", access: ["admin", "executive", "hr"] },
  website: { label: "Website", access: ["admin", "marketing"] },
  executive: { label: "Executive", access: ["admin", "executive"] },
  settings: { label: "Settings", access: ["admin"], locked: "admin" },
} as const satisfies Record<string, ApiModuleDef>;

export type ApiModuleId = keyof typeof API_MODULE_TABLE;
/** The same table, widened: every row reads as an ApiModuleDef, ids stay literal. */
export const API_MODULES: Record<ApiModuleId, ApiModuleDef> = API_MODULE_TABLE;
export const API_MODULE_IDS = Object.keys(API_MODULES) as ApiModuleId[];

/**
 * The visible navigation groups, in the order they are drawn. This is the
 * information architecture James approved for R1: the old Today/Work/Sell/Buy/
 * Company grouping described the codebase, not the business. A group exists
 * here only when a real screen sits inside it — Changes, Cost Control,
 * Billing/Draws, AP and Procurement are deliberately absent until they are
 * built, because an empty destination is a broken promise. Schedule joined in
 * R3, when it became a screen rather than an intention.
 *
 * `Admin` left in T15. It was a heading over exactly one row, and the only
 * people who could see the row already had a Settings button at the bottom of
 * the sidebar — so it cost a heading's height and told nobody anything. See
 * STANDALONE_ROUTES below for where Settings went.
 */
export const NAV_GROUPS = ["Today", "Sell", "Build", "Money", "Resources", "Company"] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

export interface NavSectionDef {
  /** The nav id, which is also the route: `/<id>`. */
  id: string;
  label: string;
  /** The API module this section is a front door onto. */
  api: ApiModuleId;
  /** The visible group this section is drawn under. */
  group: NavGroup;
}

/**
 * Every section the web shell can show, in the order it is drawn. Several are
 * front doors onto another module's API — Inbox, Tasks, Calendar, Pipeline and
 * Customers all read `/api/sales`, Jobs reads `/api/projects` — which is why a
 * section can never carry its own role list: it would promise access the API
 * then refuses.
 *
 * Order inside a group is this array's order. There is no separate `order`
 * number to keep in sync with anything: the list you read is the list you see.
 */
export const NAV_SECTIONS = [
  { id: "home", label: "Home", api: "home", group: "Today" },
  { id: "time", label: "Time", api: "time", group: "Today" },
  { id: "inbox", label: "Inbox", api: "sales", group: "Today" },
  { id: "tasks", label: "Tasks", api: "sales", group: "Today" },
  { id: "calendar", label: "Calendar", api: "sales", group: "Today" },
  { id: "sales", label: "Pipeline", api: "sales", group: "Sell" },
  { id: "customers", label: "Customers", api: "sales", group: "Sell" },
  // Relabelled for navigation only. The route stays `/estimating`, the module
  // stays `@jaks/module-estimating`, and the Permissions matrix still calls the
  // API module "Estimating" — renaming a URL would strand every saved link.
  { id: "estimating", label: "Estimates", api: "estimating", group: "Sell" },
  { id: "jobs", label: "Jobs", api: "projects", group: "Build" },
  { id: "schedule", label: "Schedule", api: "projects", group: "Build" },
  { id: "field", label: "Field", api: "field", group: "Build" },
  { id: "reports", label: "Reports", api: "reports", group: "Money" },
  { id: "vendors", label: "Vendors", api: "vendors", group: "Resources" },
  { id: "fleet", label: "Fleet", api: "fleet", group: "Resources" },
  { id: "people", label: "People", api: "people", group: "Company" },
  { id: "hr", label: "HR", api: "hr", group: "Company" },
  { id: "learn", label: "Learn", api: "learn", group: "Company" },
  { id: "website", label: "Website", api: "website", group: "Company" },
  { id: "executive", label: "Executive", api: "executive", group: "Company" },
] as const satisfies readonly NavSectionDef[];

export const NAV_SECTION_IDS = NAV_SECTIONS.map((s) => s.id);

/**
 * Real addresses with real screens that are NOT navigation.
 *
 * `/settings` is the only one, and it is deliberate. It is a full route with
 * the shell around it and it is gated by the same `allowedModules` everything
 * else is — what it stopped being in T15 is a row in a list. The admin-only
 * Settings button at the bottom of the sidebar was already the entrance
 * everybody used, non-admins never saw either, and a heading over one row cost
 * more height than the row it introduced.
 *
 * The web manifest asserts MODULES against NAV_SECTIONS **plus this list**, so
 * a route can be dropped from navigation without being dropped from the app —
 * and cannot be dropped from the app by accident, because the frozen ROUTES
 * check still covers both.
 */
export interface StandaloneRouteDef extends Omit<NavSectionDef, "group"> {
  /**
   * What the screen prints above its title, where a nav section prints its
   * group. It comes from here for the same reason `groupOf` exists: a
   * hand-written word on a screen outlives the thing it was copied from, and
   * "Admin" on the Settings page after the Admin group was retired is exactly
   * that failure.
   */
  eyebrow: string;
}

export const STANDALONE_ROUTES = [
  { id: "settings", label: "Settings", api: "settings", eyebrow: "Administration" },
] as const satisfies readonly StandaloneRouteDef[];

export type StandaloneRouteId = (typeof STANDALONE_ROUTES)[number]["id"];

/** What a standalone route's screen prints above its title. */
export function eyebrowOf(id: StandaloneRouteId): string {
  return STANDALONE_ROUTES.find((s) => s.id === id)!.eyebrow;
}

/** Every address the shell mounts: navigation first, then the standalone routes. */
export const ROUTE_IDS: readonly string[] = [
  ...NAV_SECTIONS.map((s) => s.id),
  ...STANDALONE_ROUTES.map((s) => s.id),
];

/**
 * Baseline access before any admin edit, one row per API module. The Permissions
 * matrix edits stored overrides on top of this and the API gate enforces exactly
 * the same merge (apps/api/src/access.ts).
 */
export const DEFAULT_MODULE_ACCESS: Record<string, Role[]> = Object.fromEntries(
  API_MODULE_IDS.map((id) => [id, [...API_MODULES[id].access]]),
);

/** Admins always pass; an empty or missing list means everyone signed in. */
export function allowed(
  moduleId: string,
  roles: readonly string[],
  access: Record<string, readonly Role[]>,
): boolean {
  if (roles.includes("admin")) return true;
  const need = access[moduleId];
  if (!need || need.length === 0) return true;
  return need.some((r) => roles.includes(r));
}

/**
 * Every id this person may enter: the API modules they pass the gate for, plus
 * the nav sections those modules back. `/api/me` returns this and the shell
 * renders exactly these.
 */
export function allowedModulesFor(
  roles: readonly string[],
  access: Record<string, readonly Role[]>,
): string[] {
  const api = API_MODULE_IDS.filter((id) => allowed(id, roles, access));
  const open = new Set<string>(api);
  for (const s of NAV_SECTIONS) if (open.has(s.api)) open.add(s.id);
  // A standalone route is not navigation, but it is still a section somebody
  // may open. Leaving these out would make the shell refuse /settings to the
  // admins it was just moved out of the sidebar for.
  for (const s of STANDALONE_ROUTES) if (open.has(s.api)) open.add(s.id);
  return [...open];
}

/** The sections an API module is the front door for, in nav order. */
export function navSectionsOf(api: ApiModuleId): readonly NavSectionDef[] {
  return NAV_SECTIONS.filter((s) => s.api === api);
}

export type NavSectionId = (typeof NAV_SECTIONS)[number]["id"];

/**
 * The canonical relative href for a nav section, e.g.
 * `sectionPath("time", { tab: "approvals" })` → `/time?tab=approvals`.
 *
 * Every notification, email and calendar link is built through here: the id is
 * typed against NAV_SECTIONS, so a link to a section that does not exist fails
 * to compile instead of stranding somebody on a not-found page. What the params
 * mean is each screen's own business — tab ids live with the tabs.
 */
export function sectionPath(id: NavSectionId, params?: Record<string, string>): string {
  const qs = params ? new URLSearchParams(params).toString() : "";
  return qs ? `/${id}?${qs}` : `/${id}`;
}

/* ------------------------------------------------- navigation shape */

/** A group with the sections a particular person may actually open. */
export interface NavGroupView {
  group: NavGroup;
  sections: readonly NavSectionDef[];
}

/**
 * The navigation this person sees: group order from `NAV_GROUPS`, section order
 * from `NAV_SECTIONS`, and nothing they cannot open. A group with no permitted
 * section is left out entirely rather than drawn as an empty heading — the
 * shell, the phone's More page and the tests all read this one function, so
 * they cannot disagree about what is on screen.
 */
export function navGroupsFor(allowedIds: readonly string[] | undefined): NavGroupView[] {
  if (!allowedIds) return [];
  const open = new Set(allowedIds);
  const out: NavGroupView[] = [];
  for (const group of NAV_GROUPS) {
    const sections = NAV_SECTIONS.filter((s) => s.group === group && open.has(s.id));
    if (sections.length) out.push({ group, sections });
  }
  return out;
}

/**
 * The group a section sits under, for a screen that wants to name where it is.
 * Every module page prints this above its title, so it has to come from the
 * registry: a hand-written "Work" on the Field page outlives the group it was
 * copied from, and R1 is exactly the rename that would have stranded it.
 */
export function groupOf(id: NavSectionId): NavGroup {
  return NAV_SECTIONS.find((s) => s.id === id)!.group;
}

/** Tabs on the phone, plus More. Four is what fits at 390px without crowding. */
export const MOBILE_TAB_COUNT = 4;

/**
 * Which role decides the tab bar when somebody holds several. A root admin is
 * handed every role at session time, so `admin` has to come first or every
 * owner would get a field crew member's phone. Otherwise the more specialised
 * office role wins over the broader one: a PM who also carries `field` is a PM
 * on their phone.
 */
export const ROLE_TAB_PRECEDENCE = [
  "admin", "executive", "accounting", "hr", "pm", "sales", "marketing", "field", "standard",
] as const satisfies readonly Role[];

/**
 * The four tabs each role gets, before permissions are applied. Every id here
 * is a section that role can actually open under `DEFAULT_MODULE_ACCESS`; a
 * registry test proves it, because a tab onto a section the API refuses is the
 * exact failure the capability registry was built to end.
 *
 * `standard` and `field` are the same list on purpose. Most crew members carry
 * no named role at all — they are `standard` — and the phone they pick up has
 * to be the field phone either way.
 */
export const MOBILE_TABS: Record<Role, readonly NavSectionId[]> = {
  standard: ["home", "time", "jobs", "field"],
  field: ["home", "time", "jobs", "field"],
  sales: ["home", "inbox", "sales", "customers"],
  pm: ["home", "jobs", "field", "tasks"],
  hr: ["home", "hr", "people", "time"],
  accounting: ["home", "reports", "vendors", "time"],
  marketing: ["home", "website", "inbox", "reports"],
  executive: ["home", "executive", "reports", "jobs"],
  admin: ["home", "jobs", "sales", "time"],
};

/** The role whose tab bar this person gets. */
export function tabRoleFor(roles: readonly string[] | undefined): Role {
  for (const r of ROLE_TAB_PRECEDENCE) if (roles?.includes(r)) return r;
  return "standard";
}

/**
 * The phone's tab bar. The role's list first, then anything it lost to an admin
 * override is replaced from the top of that person's own navigation, so the bar
 * is always full and never holds a tab the API would refuse. More is not in
 * here; the shell always draws it last, and everything left over lives there.
 */
export function mobileTabsFor(
  roles: readonly string[] | undefined,
  allowedIds: readonly string[] | undefined,
): NavSectionDef[] {
  if (!allowedIds) return [];
  const open = new Set(allowedIds);
  const byId = new Map(NAV_SECTIONS.map((s) => [s.id as string, s as NavSectionDef]));
  const picked: NavSectionDef[] = [];
  const add = (s: NavSectionDef | undefined) => {
    if (s && open.has(s.id) && !picked.some((p) => p.id === s.id)) picked.push(s);
  };
  for (const id of MOBILE_TABS[tabRoleFor(roles)]) add(byId.get(id));
  for (const s of NAV_SECTIONS) {
    if (picked.length >= MOBILE_TAB_COUNT) break;
    add(s);
  }
  return picked.slice(0, MOBILE_TAB_COUNT);
}

/**
 * What an API module opens, named the way navigation names it. The Permissions
 * matrix says "Sales" but the person reading it is looking for Pipeline under
 * Sell and Inbox under Today, so the row has to speak the same language as the
 * sidebar. Empty for a module with no screen of its own.
 */
export function coverageOf(api: ApiModuleId): NavGroupView[] {
  const mine = NAV_SECTIONS.filter((s) => s.api === api);
  const out: NavGroupView[] = [];
  for (const group of NAV_GROUPS) {
    const sections = mine.filter((s) => s.group === group);
    if (sections.length) out.push({ group, sections });
  }
  return out;
}

/**
 * The same thing in words, one line per group: `Sell → Pipeline, Customers`.
 * The Permissions matrix prints these, so the sentence an admin reads before
 * taking Sales away from somebody is built here and tested here rather than
 * assembled inline in a table cell.
 *
 * A standalone route contributes a line with no group in front of it, because
 * it has no group — `Settings` rather than `Admin → Settings`. Without that the
 * matrix would print "Background only — no screen of its own" against the one
 * module whose screen an admin is most likely to be looking at while they read
 * it, which is the sort of small lie a caption never recovers from.
 */
export function describeCoverage(api: ApiModuleId): string[] {
  return [
    ...coverageOf(api).map((g) => `${g.group} → ${g.sections.map((s) => s.label).join(", ")}`),
    ...STANDALONE_ROUTES.filter((s) => s.api === api).map((s) => s.label),
  ];
}

/**
 * Keep only the rows this registry knows and lets an admin change. A stale key
 * left over from an older matrix must not gate anything, and a locked row must
 * come from here even if the stored value says otherwise — otherwise a hand-
 * edited `settings: []` in the database would open Settings to the whole team.
 */
/**
 * The effective access map: the registry's defaults with an admin's stored
 * overrides on top. The API gate and any module that needs to ask "may this
 * person open X?" both call this, so a second, subtly different merge cannot
 * grow somewhere else and quietly disagree with the gate.
 */
export function mergeModuleAccess(
  stored: Record<string, unknown> | undefined,
): Record<string, Role[]> {
  return { ...DEFAULT_MODULE_ACCESS, ...knownModuleAccess(stored ?? {}) };
}

export function knownModuleAccess(
  stored: Record<string, unknown>,
): Record<string, Role[]> {
  const out: Record<string, Role[]> = {};
  for (const id of API_MODULE_IDS) {
    if (API_MODULES[id].locked) continue;
    const v = stored[id];
    if (!Array.isArray(v)) continue;
    // A retired role and a typo both fail this test, and both should: the
    // stored row must never gate anything by a name this registry no longer
    // knows. RETIRED_ROLES exists so the WRITE path can tell them apart.
    const live = v.filter((r): r is Role => (ROLES as readonly string[]).includes(r));
    // An EMPTY row means "everyone", so a row that named roles and has just
    // lost all of them must not become one — that would open a section wider
    // than the admin who wrote it ever asked for. Dropping the row instead
    // falls back to this registry's own default, which is the safe reading.
    if (live.length === 0 && v.length > 0) continue;
    out[id] = live;
  }
  return out;
}
